import { FLAGS } from '@gozoki/feature-flags'
import { FORECAST_REFERENCE_QUERY_OPTIONS } from '~/lib/forecast-reference-queries'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute, redirect } from '@tanstack/react-router'
import { type JSX, useCallback, useEffect, useMemo, useState } from 'react'
import { AppContainer } from '~/components/new/app-ui/app-container'
import { AppHeader } from '~/components/new/app-ui/app-header'
import { Title } from '~/components/new/app-ui/title'
import {
  type DashboardCardLoadState,
  resolveDashboardCardState,
  retryFailedDashboardQueries,
} from '~/components/dashboards/dashboard-card-state'
import { Button } from '~/components/ui/button'
import { InfoTooltip } from '~/components/ui/info-tooltip'
import { useFeatureFlag } from '~/hooks/useFeatureFlag'
import { api } from '~/lib/tuyau'
import { withRoutePermissions } from '~/utils/rbac'
import { BoutiqueChartModal, type BoutiqueScope } from './-boutique-chart-modal'
import { PrevisionsExportButton } from './-export-xlsx-button'
import type { PrevisionsExportInputs } from './-previsions-export-data'
import { useProductBucketsLoader } from './-previsions-product-buckets'
import { PREVISIONS_QUERY_OPTIONS } from './-previsions-query'
import { BoutiquesCard } from './-boutiques-card'
import { CategoriesTable } from './-categories-table'
import { FiltersBar } from './-filters-bar'
import {
  type AxisTotalsByGroupBy,
  buildCategoryTree,
  buildCategoryTreeFromTotals,
  buildProjectRows,
  buildStoreRows,
  buildTotals,
  buildTotalsFromAxis,
  type ForecastCategory,
} from './-previsions-data'
import {
  dayAndMonth,
  FORECAST_METHOD_LINES,
  FORECAST_MONTH_METHOD_LINES,
  FORECAST_WEEK_METHOD_LINES,
} from './-previsions-display'
import {
  type BucketProvenance,
  bucketRange,
  bucketStartOf,
  type Granularity,
  monthLongLabel,
  monthsOfRange,
  provenanceByMonthOf,
  provenanceByWeekOf,
  rangeForGranularity,
  resolveGranularity,
  type Series,
  seriesFromDays,
  seriesFromMonths,
  seriesFromWeeks,
  weekIsoLabel,
  weeksOfRange,
} from './-previsions-granularity'
import { PrevisionsCardBoundary, PrevisionsCardSkeleton } from './-previsions-skeleton'
import { ProductionChart } from './-production-chart'
import {
  isDateInRange,
  PREVISIONS_EXPORT_GROUP_BYS,
  previsionsSearchSchema,
  resolvePrevisionsRange,
  SEGMENT_CATEGORY_SOURCE,
  SEGMENT_GROUP_BY,
  selectedProjectIdsOf,
  selectedStoreIdsOf,
  type CategorySource,
  type Segment,
} from './-search-schema'
import { TotauxCard } from './-totaux-card'

export const Route = createFileRoute('/_authenticated/previsions/')({
  validateSearch: previsionsSearchSchema,
  staticData: { permission: ['read', 'previsions.previsions.forecast'] },
  beforeLoad: ({ search }) => {
    if (!search.focusDate) return
    if (isDateInRange(search.focusDate, resolvePrevisionsRange(search))) return
    throw redirect({
      to: '/previsions',
      replace: true,
      search: { ...search, focusDate: undefined },
    })
  },
  component: PrevisionsProductionPage,
})

withRoutePermissions(Route)

const PAGE_CLASS = 'flex h-[calc(100vh-1rem)] min-h-0 flex-col'
const HEADER_CLASS = 'shrink-0'
const TITLE_CLASS = 'font-brand text-[32px] font-extrabold leading-[36px] text-[#072c1a]'
const CONTAINER_CLASS = 'min-h-0 gap-2 rounded-none border-0 bg-transparent p-0'
const GRID_COLUMNS = '376px minmax(0, 1fr)'
const NO_WEEKS: string[] = []
const NO_MONTHS: string[] = []
const METHOD_LINES: Record<Granularity, readonly string[]> = {
  day: FORECAST_METHOD_LINES,
  week: FORECAST_WEEK_METHOD_LINES,
  month: FORECAST_MONTH_METHOD_LINES,
}

const coversAll = (selected: readonly number[], all: readonly number[]): boolean =>
  all.length > 0 && all.every((id) => selected.includes(id))

const bucketsOf = (
  mode: Granularity | 'pending',
  weeks: string[],
  months: string[],
): string[] | undefined => {
  if (mode === 'month') return months
  if (mode === 'week') return weeks
  return undefined
}

function PrevisionsProductionPage() {
  const enabled = useFeatureFlag(FLAGS.forecastingXiuDashboard)
  const kitchenFilterEnabled = useFeatureFlag(FLAGS.configurateurIndustriel)
  const search = Route.useSearch()
  const navigate = Route.useNavigate()
  const [dailyScope, setDailyScope] = useState<BoutiqueScope | null>(null)

  const setSearch = (next: Partial<typeof search>) =>
    navigate({ search: (prev) => ({ ...prev, ...next }) })

  const setFocusDate = useCallback(
    (focusDate: string | undefined) => navigate({ search: (prev) => ({ ...prev, focusDate }) }),
    [navigate],
  )

  const range = resolvePrevisionsRange(search)
  const focusDate = search.focusDate

  const coverageQuery = useQuery(
    api.forecast.forecastCoverage.queryOptions({}, { enabled, ...PREVISIONS_QUERY_OPTIONS }),
  )
  const coverage = coverageQuery.data
  const mode: Granularity | 'pending' = coverageQuery.isError
    ? 'day'
    : resolveGranularity(range, coverage)
  const granularity: Granularity = mode === 'pending' ? 'day' : mode
  const effectiveRange = rangeForGranularity(range, granularity)
  const effectiveStart = effectiveRange.start
  const effectiveEnd = effectiveRange.end
  const weeks = useMemo(
    () => (mode === 'week' ? weeksOfRange({ start: effectiveStart, end: effectiveEnd }) : NO_WEEKS),
    [mode, effectiveStart, effectiveEnd],
  )
  const months = useMemo(
    () =>
      mode === 'month' ? monthsOfRange({ start: effectiveStart, end: effectiveEnd }) : NO_MONTHS,
    [mode, effectiveStart, effectiveEnd],
  )

  const snappedFocus = focusDate === undefined ? undefined : bucketStartOf(focusDate, granularity)

  useEffect(() => {
    if (mode === 'pending' || mode === 'day') return
    const rangeSettled = range.start === effectiveStart && range.end === effectiveEnd
    if (rangeSettled && snappedFocus === focusDate) return
    navigate({
      search: (prev) => ({
        ...prev,
        startDate: effectiveStart,
        endDate: effectiveEnd,
        focusDate: snappedFocus,
      }),
      replace: true,
    })
  }, [
    mode,
    range.start,
    range.end,
    effectiveStart,
    effectiveEnd,
    focusDate,
    snappedFocus,
    navigate,
  ])

  const storesQuery = useQuery(
    api.forecast.forecastStores.queryOptions({}, FORECAST_REFERENCE_QUERY_OPTIONS),
  )
  const stores = storesQuery.data ?? []
  const categoriesQuery = useQuery(
    api.forecast.productCategories.queryOptions(
      {},
      { enabled, ...FORECAST_REFERENCE_QUERY_OPTIONS },
    ),
  )
  const categories = categoriesQuery.data ?? []
  const segmentCategoriesQuery = useQuery(
    api.forecast.segmentCategories.queryOptions(
      {},
      { enabled, ...FORECAST_REFERENCE_QUERY_OPTIONS },
    ),
  )
  const projectsQuery = useQuery(
    api.forecast.forecastProjects.queryOptions(
      {},
      { enabled, ...FORECAST_REFERENCE_QUERY_OPTIONS },
    ),
  )
  const kitchensQuery = useQuery(
    api.forecast.centralKitchens.queryOptions(
      {},
      { enabled: enabled && kitchenFilterEnabled, ...FORECAST_REFERENCE_QUERY_OPTIONS },
    ),
  )

  const axisSource = SEGMENT_CATEGORY_SOURCE[search.segment]
  const axisCategoriesQuery = axisSource === 'commercial' ? categoriesQuery : segmentCategoriesQuery
  const categoriesBySource: Record<CategorySource, ForecastCategory[]> = {
    commercial: categories,
    internal: segmentCategoriesQuery.data?.internal ?? [],
    reappro: segmentCategoriesQuery.data?.reappro ?? [],
  }
  const axisCategories = categoriesBySource[axisSource]

  const setSegment = (segment: Segment) => {
    if (segment === search.segment) return
    setSearch({ segment, categoryIds: [] })
  }

  const filtersByCategory = search.categoryIds.length > 0
  const selectedCategories = axisCategories.filter((category) =>
    search.categoryIds.includes(category.id),
  )
  const productIds = selectedCategories.flatMap((category) =>
    category.products.map((product) => product.id),
  )
  const appliedCategoryIds =
    productIds.length > 0 ? selectedCategories.map((category) => category.id) : search.categoryIds

  const scopeLoading = filtersByCategory && axisCategoriesQuery.isPending
  const scopeFailed = filtersByCategory && axisCategoriesQuery.isError
  const scopeEmpty = filtersByCategory && !axisCategoriesQuery.isPending && productIds.length === 0

  const allStoreIds = stores.map((store) => store.storeId)
  const scopedStoreIds = selectedStoreIdsOf(search, allStoreIds)

  const projects = projectsQuery.data ?? []
  const includeProjects = search.includeProjects
  const allProjectIds = projects.map((project) => project.storeProjectId)
  const scopedProjectIds = selectedProjectIdsOf(search, allProjectIds)

  const applyScope = (storeIds: number[], projectIds: number[]) => {
    const dropsAllProjects = includeProjects && projectIds.length === 0
    setSearch({
      storeIds: coversAll(storeIds, allStoreIds) ? undefined : storeIds,
      projectIds: dropsAllProjects || coversAll(projectIds, allProjectIds) ? undefined : projectIds,
      ...(dropsAllProjects ? { includeProjects: false } : {}),
    })
  }

  const kitchens = kitchensQuery.data ?? []
  const selectedKitchenIds = kitchenFilterEnabled ? search.kitchenIds : []
  const filtersByKitchen = selectedKitchenIds.length > 0
  const appliedKitchenIds = selectedKitchenIds.filter((kitchenId) =>
    kitchens.some((kitchen) => kitchen.id === kitchenId),
  )

  const storeScopeLoading = storesQuery.isPending
  const projectScopeLoading =
    includeProjects && search.projectIds === undefined && projectsQuery.isPending
  const projectScopeFailed = includeProjects && projectsQuery.isError
  const selectionEmpty =
    !storeScopeLoading &&
    !projectScopeLoading &&
    scopedStoreIds.length === 0 &&
    scopedProjectIds.length === 0

  const kitchenScopeLoading = filtersByKitchen && kitchensQuery.isPending
  const kitchenScopeFailed = filtersByKitchen && kitchensQuery.isError
  const kitchenScopeEmpty =
    filtersByKitchen && kitchensQuery.isSuccess && appliedKitchenIds.length === 0

  const queriesEnabled =
    enabled &&
    !storeScopeLoading &&
    !selectionEmpty &&
    !scopeLoading &&
    !scopeEmpty &&
    !kitchenScopeLoading &&
    !kitchenScopeEmpty &&
    !projectScopeLoading &&
    !projectScopeFailed

  const scopeQuery = {
    storeIds: scopedStoreIds,
    ...(productIds.length > 0 ? { productIds } : {}),
    ...(appliedKitchenIds.length > 0 ? { centralKitchenIds: appliedKitchenIds } : {}),
    ...(scopedProjectIds.length > 0 ? { projectIds: scopedProjectIds } : {}),
  }

  const dailyEnabled = queriesEnabled && mode === 'day'
  const weeklyEnabled = queriesEnabled && mode === 'week' && weeks.length > 0
  const monthlyEnabled = queriesEnabled && mode === 'month' && months.length > 0

  const axisGroupBy = SEGMENT_GROUP_BY[search.segment]
  const summaryRange = focusDate ? bucketRange(focusDate, granularity) : effectiveRange
  const focusBucket = snappedFocus
  const startWeek = focusBucket ?? weeks[0] ?? effectiveRange.start
  const endWeek = focusBucket ?? weeks[weeks.length - 1] ?? effectiveRange.start
  const seriesStartMonth = months[0] ?? effectiveRange.start
  const seriesEndMonth = months[months.length - 1] ?? effectiveRange.start
  const startMonth = focusBucket ?? seriesStartMonth
  const endMonth = focusBucket ?? seriesEndMonth

  const forecastQuery = useQuery(
    api.forecast.shortTermSummary.queryOptions(
      {
        query: {
          ...scopeQuery,
          startDate: summaryRange.start,
          endDate: summaryRange.end,
          capping: search.capping,
        },
      },
      { enabled: dailyEnabled, ...PREVISIONS_QUERY_OPTIONS },
    ),
  )

  const weeklySummaryQuery = useQuery(
    api.forecast.weeklyTableSummary.queryOptions(
      {
        query: {
          ...scopeQuery,
          startWeek,
          endWeek,
          capping: search.capping,
        },
      },
      { enabled: weeklyEnabled, ...PREVISIONS_QUERY_OPTIONS },
    ),
  )

  const axisReferenceQuery = useQuery(
    api.forecast.shortTermDailySeries.queryOptions(
      {
        query: {
          ...scopeQuery,
          startDate: effectiveRange.start,
          endDate: effectiveRange.end,
          groupBy: axisGroupBy,
          capping: !search.capping,
        },
      },
      { enabled: dailyEnabled, ...PREVISIONS_QUERY_OPTIONS },
    ),
  )

  const axesQuery = useQuery(
    api.forecast.shortTermDailySeriesAxes.queryOptions(
      {
        query: {
          ...scopeQuery,
          startDate: effectiveRange.start,
          endDate: effectiveRange.end,
          groupBys: PREVISIONS_EXPORT_GROUP_BYS,
          capping: search.capping,
        },
      },
      { enabled: dailyEnabled, ...PREVISIONS_QUERY_OPTIONS },
    ),
  )

  const weeklyAxisReferenceQuery = useQuery(
    api.forecast.weeklyTableSeries.queryOptions(
      {
        query: {
          ...scopeQuery,
          startWeek: weeks[0] ?? effectiveRange.start,
          endWeek: weeks[weeks.length - 1] ?? effectiveRange.start,
          groupBys: [axisGroupBy],
          capping: !search.capping,
        },
      },
      { enabled: weeklyEnabled, ...PREVISIONS_QUERY_OPTIONS },
    ),
  )

  const weeklyAxesQuery = useQuery(
    api.forecast.weeklyTableSeries.queryOptions(
      {
        query: {
          ...scopeQuery,
          startWeek: weeks[0] ?? effectiveRange.start,
          endWeek: weeks[weeks.length - 1] ?? effectiveRange.start,
          groupBys: PREVISIONS_EXPORT_GROUP_BYS,
          capping: search.capping,
        },
      },
      { enabled: weeklyEnabled, ...PREVISIONS_QUERY_OPTIONS },
    ),
  )

  const monthlySummaryQuery = useQuery(
    api.forecast.monthlyTableSummary.queryOptions(
      {
        query: {
          ...scopeQuery,
          startMonth,
          endMonth,
        },
      },
      { enabled: monthlyEnabled, ...PREVISIONS_QUERY_OPTIONS },
    ),
  )

  const monthlyAxesQuery = useQuery(
    api.forecast.monthlyTableSeries.queryOptions(
      {
        query: {
          ...scopeQuery,
          startMonth: seriesStartMonth,
          endMonth: seriesEndMonth,
          groupBys: PREVISIONS_EXPORT_GROUP_BYS,
        },
      },
      { enabled: monthlyEnabled, ...PREVISIONS_QUERY_OPTIONS },
    ),
  )

  const loadProductBuckets = useProductBucketsLoader(
    scopeQuery,
    effectiveRange,
    search.capping,
    granularity,
  )

  const isMonth = mode === 'month'
  const isWeek = mode === 'week'
  const bucketSummaryQuery = isWeek ? weeklySummaryQuery : forecastQuery
  const bucketSeriesQuery = isWeek ? weeklyAxesQuery : axesQuery
  const summaryQuery = isMonth ? monthlySummaryQuery : bucketSummaryQuery
  const seriesQuery = isMonth ? monthlyAxesQuery : bucketSeriesQuery

  const storeLabels = new Map(stores.map((store) => [store.storeId, store.label]))

  const monthlySummaryData = isMonth ? monthlySummaryQuery.data : undefined
  const bucketSummaryData = isMonth ? undefined : bucketSummaryQuery.data
  const summaryData = isMonth ? monthlySummaryData : bucketSummaryData
  const forecastRows = bucketSummaryData?.products ?? []
  const summaryStores = summaryData?.stores ?? []
  const storeRows = buildStoreRows(summaryStores, storeLabels)
  const summaryProjects = summaryData?.projects ?? []
  const projectRows = buildProjectRows(summaryProjects)
  const zeroShareProducts = bucketSummaryData?.zeroShareProducts

  const axisTotals: AxisTotalsByGroupBy = useMemo(
    () => monthlySummaryData?.axisTotals ?? {},
    [monthlySummaryData],
  )

  const seriesByAxis: Partial<Record<typeof axisGroupBy, Series[]>> = useMemo(() => {
    const axes = seriesQuery.data?.seriesByAxis
    if (axes === undefined) return {}
    const convert = (entries: unknown): Series[] => {
      if (mode === 'month')
        return seriesFromMonths(entries as Parameters<typeof seriesFromMonths>[0])
      if (mode === 'week') return seriesFromWeeks(entries as Parameters<typeof seriesFromWeeks>[0])
      return seriesFromDays(entries as Parameters<typeof seriesFromDays>[0])
    }
    return Object.fromEntries(
      Object.entries(axes).map(([axis, entries]) => [axis, convert(entries)]),
    )
  }, [seriesQuery.data, mode])

  const axisReferenceSeries: Series[] | undefined = useMemo(() => {
    if (mode === 'month') return undefined
    if (mode === 'week') {
      const axes = weeklyAxisReferenceQuery.data?.seriesByAxis
      if (axes === undefined) return undefined
      return seriesFromWeeks(axes[axisGroupBy] ?? [])
    }
    if (axisReferenceQuery.data === undefined) return undefined
    return seriesFromDays(axisReferenceQuery.data.series)
  }, [mode, axisGroupBy, weeklyAxisReferenceQuery.data, axisReferenceQuery.data])

  const weekMethods = weeklyAxesQuery.data?.weeks ?? weeklySummaryQuery.data?.weeks
  const monthInfos = monthlyAxesQuery.data?.months ?? monthlySummaryQuery.data?.months
  const provenanceByBucket = useMemo<Map<string, BucketProvenance>>(
    () => (isMonth ? provenanceByMonthOf(monthInfos ?? []) : provenanceByWeekOf(weekMethods ?? [])),
    [isMonth, monthInfos, weekMethods],
  )

  const exportInputs: Omit<PrevisionsExportInputs, 'productBuckets'> = {
    search,
    range: effectiveRange,
    granularity,
    stores,
    forecastRows,
    forecastStores: summaryStores,
    projects: projectRows,
    forecastProjects: summaryProjects,
    zeroShareProducts,
    categoriesBySource,
    kitchens,
    appliedKitchenIds,
    seriesByAxis,
    axisTotals,
    provenanceByBucket: granularity === 'day' ? undefined : provenanceByBucket,
  }

  const summaryIsEmpty = isMonth
    ? summaryStores.length === 0 && summaryProjects.length === 0
    : forecastRows.length === 0
  const axisBuckets = bucketsOf(mode, weeks, months)

  const totals = isMonth
    ? buildTotalsFromAxis(summaryStores, axisTotals.uf ?? [], summaryProjects)
    : buildTotals(forecastRows)

  const treeSegment = search.segment === 'uf' ? 'commercial' : search.segment
  const categoryTree = isMonth
    ? buildCategoryTreeFromTotals(axisTotals[SEGMENT_GROUP_BY[treeSegment]] ?? [], axisCategories)
    : buildCategoryTree(forecastRows, axisCategories, treeSegment)

  const referenceDateFor = (state: DashboardCardLoadState): string | undefined =>
    state === 'transitioning' ? undefined : focusDate

  const totauxState = resolveDashboardCardState(summaryQuery)
  const boutiquesState = resolveDashboardCardState(summaryQuery, storesQuery)
  const categoriesState = resolveDashboardCardState(summaryQuery, axisCategoriesQuery)
  const chartState = resolveDashboardCardState(seriesQuery, axisCategoriesQuery)
  const exportState = resolveDashboardCardState(
    summaryQuery,
    seriesQuery,
    storesQuery,
    categoriesQuery,
    segmentCategoriesQuery,
  )

  const emptyBucketNotice = (bucket: string): string => {
    if (granularity === 'month') {
      return `Aucune production prévue en ${monthLongLabel(bucket)} sur ce périmètre.`
    }
    if (granularity === 'week') {
      return `Aucune production prévue en ${weekIsoLabel(bucket)} sur ce périmètre.`
    }
    return `Aucune production prévue le ${dayAndMonth(bucket)} sur ce périmètre.`
  }

  const withEmptyDayNotice = (emptyDate: string | undefined, children: JSX.Element) => {
    if (emptyDate === undefined) return children
    return (
      <>
        <div className="flex shrink-0 items-center gap-2">
          <p className="text-muted-foreground">{emptyBucketNotice(emptyDate)}</p>
          <Button variant="outline" size="sm" onClick={() => setFocusDate(undefined)}>
            Revenir à la période complète
          </Button>
        </div>
        {children}
      </>
    )
  }

  const grid = (children: JSX.Element) => (
    <div
      className="grid min-h-0 flex-1 gap-2"
      style={{ gridTemplateColumns: GRID_COLUMNS, gridTemplateRows: 'auto minmax(0, 1fr)' }}
    >
      {children}
    </div>
  )

  const content = () => {
    if (
      mode === 'pending' ||
      storeScopeLoading ||
      scopeLoading ||
      kitchenScopeLoading ||
      projectScopeLoading
    ) {
      return grid(
        <>
          <PrevisionsCardSkeleton variant="totaux" />
          <PrevisionsCardSkeleton variant="chart" />
          <PrevisionsCardSkeleton variant="boutiques" />
          <PrevisionsCardSkeleton variant="categories" />
        </>,
      )
    }
    if (selectionEmpty) {
      return (
        <div className="flex flex-col items-start gap-2">
          <p className="text-muted-foreground">Aucune boutique ni aucun projet sélectionné.</p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setSearch({ storeIds: undefined, projectIds: undefined })}
          >
            Tout sélectionner
          </Button>
        </div>
      )
    }
    if (scopeFailed) {
      return (
        <div className="flex flex-col items-start gap-2">
          <p className="text-amber-600">
            Erreur lors du chargement des catégories : le filtre de l’URL ne peut pas être appliqué.
          </p>
          <Button variant="outline" size="sm" onClick={() => axisCategoriesQuery.refetch()}>
            Réessayer
          </Button>
        </div>
      )
    }
    if (scopeEmpty) {
      return (
        <div className="flex flex-col items-start gap-2">
          <p className="text-muted-foreground">
            Les catégories filtrées ne contiennent aucun produit actif : aucune prévision ne peut
            être calculée sur ce périmètre.
          </p>
          <Button variant="outline" size="sm" onClick={() => setSearch({ categoryIds: [] })}>
            Réinitialiser les catégories
          </Button>
        </div>
      )
    }
    if (projectScopeFailed) {
      return (
        <div className="flex flex-col items-start gap-2">
          <p className="text-amber-600">
            Erreur lors du chargement des projets d’ouverture : ils ne peuvent pas être ajoutés à la
            prévision.
          </p>
          <Button variant="outline" size="sm" onClick={() => projectsQuery.refetch()}>
            Réessayer
          </Button>
        </div>
      )
    }
    if (kitchenScopeFailed) {
      return (
        <div className="flex flex-col items-start gap-2">
          <p className="text-amber-600">
            Erreur lors du chargement des cuisines centrales : le filtre de l’URL ne peut pas être
            appliqué.
          </p>
          <Button variant="outline" size="sm" onClick={() => kitchensQuery.refetch()}>
            Réessayer
          </Button>
        </div>
      )
    }
    if (kitchenScopeEmpty) {
      return (
        <div className="flex flex-col items-start gap-2">
          <p className="text-muted-foreground">
            Les cuisines centrales filtrées n’existent plus : aucune prévision ne peut être calculée
            sur ce périmètre.
          </p>
          <Button variant="outline" size="sm" onClick={() => setSearch({ kitchenIds: [] })}>
            Réinitialiser les cuisines
          </Button>
        </div>
      )
    }
    if (summaryData !== undefined && summaryIsEmpty && !focusDate) {
      return <p className="text-muted-foreground">Aucune production prévue sur ce périmètre.</p>
    }

    const emptyFocusedDay = focusDate !== undefined && summaryData !== undefined && summaryIsEmpty

    return withEmptyDayNotice(
      emptyFocusedDay ? focusDate : undefined,
      grid(
        <>
          <PrevisionsCardBoundary
            state={totauxState}
            variant="totaux"
            tone="totaux"
            errorMessage="Totaux indisponibles"
            onRetry={() => retryFailedDashboardQueries(summaryQuery)}
          >
            <TotauxCard
              totals={totals}
              storeCount={storeRows.length}
              projectCount={projectRows.length}
              referenceDate={referenceDateFor(totauxState)}
              granularity={granularity}
            />
          </PrevisionsCardBoundary>

          <PrevisionsCardBoundary
            state={chartState}
            variant="chart"
            tone="chart"
            errorMessage="Séries indisponibles"
            onRetry={() => retryFailedDashboardQueries(seriesQuery, axisCategoriesQuery)}
          >
            <ProductionChart
              series={seriesByAxis[axisGroupBy] ?? []}
              axisReferenceSeries={axisReferenceSeries}
              categories={axisCategories}
              measure={search.measure}
              segment={search.segment}
              onMeasureChange={(measure) => setSearch({ measure })}
              granularity={granularity}
              axisBuckets={axisBuckets}
              provenanceByBucket={provenanceByBucket}
              focusedBucket={focusDate}
              onFocusBucketChange={setFocusDate}
            />
          </PrevisionsCardBoundary>

          <PrevisionsCardBoundary
            state={boutiquesState}
            variant="boutiques"
            tone="neutral"
            errorMessage="Boutiques indisponibles"
            onRetry={() => retryFailedDashboardQueries(summaryQuery, storesQuery)}
          >
            <BoutiquesCard
              stores={storeRows}
              projects={projectRows}
              onShowDaily={setDailyScope}
              referenceDate={referenceDateFor(boutiquesState)}
              granularity={granularity}
            />
          </PrevisionsCardBoundary>

          <PrevisionsCardBoundary
            state={categoriesState}
            variant="categories"
            tone="neutral"
            errorMessage="Catégories indisponibles"
            onRetry={() => retryFailedDashboardQueries(summaryQuery, axisCategoriesQuery)}
          >
            <CategoriesTable
              categories={categoryTree}
              rows={forecastRows}
              storeCount={storeRows.length}
              projectCount={projectRows.length}
              segment={search.segment}
              zeroShareProducts={zeroShareProducts}
              referenceDate={referenceDateFor(categoriesState)}
              granularity={granularity}
              ufTotals={isMonth ? (axisTotals.uf ?? []) : undefined}
            />
          </PrevisionsCardBoundary>
        </>,
      ),
    )
  }

  if (!enabled) {
    return (
      <div className={PAGE_CLASS}>
        <AppHeader className={HEADER_CLASS}>
          <Title className={TITLE_CLASS}>Prévisions</Title>
        </AppHeader>
        <AppContainer className={CONTAINER_CLASS}>
          <p className="text-muted-foreground">Fonctionnalité non activée.</p>
        </AppContainer>
      </div>
    )
  }

  return (
    <div className={PAGE_CLASS}>
      <AppHeader className={HEADER_CLASS}>
        <div className="flex items-center gap-2">
          <Title className={TITLE_CLASS}>Prévisions</Title>
          <InfoTooltip label="Prévisions" iconClassName="size-5" side="bottom">
            <div className="flex flex-col gap-1.5">
              {METHOD_LINES[granularity].map((line) => (
                <p key={line}>{line}</p>
              ))}
            </div>
          </InfoTooltip>
          <PrevisionsExportButton
            inputs={exportInputs}
            loadProductBuckets={loadProductBuckets}
            disabled={!queriesEnabled || exportState !== 'ready'}
          />
        </div>
      </AppHeader>
      <AppContainer className={CONTAINER_CLASS}>
        <FiltersBar
          storeIds={scopedStoreIds}
          projectIds={scopedProjectIds}
          onScopeChange={applyScope}
          range={effectiveRange}
          onRangeChange={({ start, end }) =>
            setSearch({ startDate: start, endDate: end, focusDate: undefined })
          }
          coverage={coverage}
          granularity={granularity}
          segment={search.segment}
          onSegmentChange={setSegment}
          categoryIds={appliedCategoryIds}
          onCategoryIdsChange={(categoryIds) => setSearch({ categoryIds })}
          categories={axisCategories.filter((category) => category.products.length > 0)}
          kitchenIds={selectedKitchenIds}
          onKitchenIdsChange={(nextKitchenIds) => setSearch({ kitchenIds: nextKitchenIds })}
          kitchens={kitchens}
          kitchensEnabled={kitchenFilterEnabled}
          kitchensPending={kitchensQuery.isPending}
          kitchensFailed={kitchensQuery.isError}
          includeProjects={includeProjects}
          onIncludeProjectsChange={(nextIncludeProjects) =>
            setSearch({
              includeProjects: nextIncludeProjects,
              projectIds: undefined,
              ...(!nextIncludeProjects && search.storeIds?.length === 0
                ? { storeIds: undefined }
                : {}),
            })
          }
          projects={projects}
        />
        {content()}

        {dailyScope && (
          <BoutiqueChartModal
            scope={dailyScope}
            range={effectiveRange}
            granularity={granularity}
            productIds={productIds}
            centralKitchenIds={appliedKitchenIds}
            categories={axisCategories}
            measure={search.measure}
            segment={search.segment}
            capping={search.capping}
            onClose={() => setDailyScope(null)}
          />
        )}
      </AppContainer>
    </div>
  )
}
